import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useBrand } from "../../lib/BrandContext";
import { useAuth } from "../../lib/AuthContext";
import { Button } from "../../components/ui/button";
import Confetti from "../../components/Confetti";

/**
 * Landing page for the verification link in outbound email.
 *
 * States:
 *   - verifying: awaiting server round-trip
 *   - success:   fresh verification succeeded
 *   - already:   idempotent already-verified path
 *   - failed:    invalid / expired / used token
 *
 * Post-success UX
 * ---------------
 * The server tells us WHICH user_id the token belongs to. We then compare
 * that against whoever is currently authenticated in this browser and
 * take one of three paths:
 *
 *   1. No active session       → point them at /login?verified=1 so the
 *                                 Login page shows a "Please log in" banner.
 *   2. Active session, matches → refresh AuthContext (flips
 *                                 email_verified in the app shell) and
 *                                 auto-navigate to the role dashboard
 *                                 ~1.5s later.
 *   3. Active session, DIFFERS → the token belongs to a DIFFERENT user
 *                                 than whoever is signed in on this
 *                                 device (typical: shared iPhone where
 *                                 Coach A is logged in and Coach B just
 *                                 tapped their verification email). We
 *                                 MUST NOT silently merge Coach B's
 *                                 verification into Coach A's live
 *                                 dashboard — that would expose Coach A's
 *                                 org, teams, players, fees, payments,
 *                                 receipts, reminders and settings to
 *                                 Coach B. Instead we force-log-out the
 *                                 active session and redirect to
 *                                 /login?verified=1&switched=1 so Coach B
 *                                 can log in cleanly with their own
 *                                 credentials.
 *
 * Note: server-side verification is complete regardless of which path
 * we take. The client-side switching is a UX + isolation control, not
 * a security decision — the destination route's RequireAuth guard still
 * enforces auth server-side.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const brand = useBrand();
  const { user, loading: authLoading, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({
    step: "verifying",
    email: "",
    verifiedUserId: "",
    error: "",
  });

  // React StrictMode double-mounts effects in dev. The ref keeps us from
  // firing the verify request twice — the backend is idempotent so the
  // second call would just return `already_verified:true`, but skipping
  // the wasted round-trip is nice.
  //
  // NOTE: we deliberately DO NOT gate the setState calls on a `cancelled`
  // closure flag. Under StrictMode the first mount's cleanup would flip
  // that flag to true, and the second mount would skip the fetch (due to
  // this ref), leaving the state permanently stuck on "verifying" — the
  // exact bug users hit when tapping the email link on iPhone Safari.
  // React 18+ silently no-ops `setState` on a truly unmounted component,
  // so the closure guard bought nothing except this deadlock.
  const ranRef = useRef(false);
  // Guards the auto-navigate effect so it fires at most once per mount.
  // Prevents a re-render (e.g. AuthContext refresh completing) from
  // triggering navigate twice.
  const autoNavRef = useRef(false);

  // True once we've confirmed the token's owner differs from the active
  // session. Set BEFORE we kick off logout so the render pass shows the
  // "switching accounts" hint instead of "taking you to your dashboard".
  const sessionMismatch =
    !!user && !!state.verifiedUserId && user.id !== state.verifiedUserId;

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    (async () => {
      if (!token) {
        setState({ step: "failed", email: "", verifiedUserId: "", error: "This link is missing its verification token." });
        return;
      }
      try {
        const { data } = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        setState({
          step: data.already_verified ? "already" : "success",
          email: data.email || "",
          verifiedUserId: data.user_id || "",
          error: "",
        });
        // Session/token isolation guard.
        //
        // If a session is active in THIS tab AND it belongs to the same
        // user whose token we just verified, refresh AuthContext so
        // `user.email_verified` flips true and the AppLayout banner
        // disappears without a hard reload.
        //
        // If the active session belongs to a DIFFERENT user, we
        // intentionally SKIP the refresh + storage bump here — the
        // auto-nav effect below will force-logout and redirect to
        // /login instead. Refreshing would re-fetch Coach A's /auth/me
        // (wasteful) and bumping storage would tell OTHER Coach A tabs
        // to refetch as if their email was verified (confusing).
        if (user && data.user_id && user.id === data.user_id) {
          try { await refresh(); } catch { /* ignore */ }
          // Broadcast a signal to any OTHER tabs where the same coach may
          // still be logged in (typical iPhone flow: sign up in tab A, tap
          // email link in tab B). The "storage" event fires in every other
          // same-origin tab; AuthContext listens for it and re-fetches
          // /auth/me so the banner disappears when the coach returns to
          // tab A. Same-tab refresh is already handled above.
          try {
            window.localStorage.setItem("sf_auth_state_bump", String(Date.now()));
          } catch { /* localStorage disabled — no-op */ }
        }
      } catch (e) {
        const detail = formatApiErrorDetail(e.response?.data?.detail) || "This verification link is invalid or has expired.";
        setState({ step: "failed", email: "", verifiedUserId: "", error: detail });
      }
    })();
    // Single-fire on mount: `user`/`refresh` are read inside the effect only
    // to opportunistically refresh AuthContext when the session matches
    // the verified token; adding them to deps would re-fire the verify
    // request every time AuthContext.check() completes and hand us a new
    // `user` object identity — the ranRef guard would then no-op every
    // extra run, which is fine, but the eslint rule flags it anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Post-success auto-navigation. Runs after BOTH:
  //   (1) the verify request has resolved (state.step in {'success','already'}), AND
  //   (2) AuthContext has finished its initial check (authLoading === false)
  // so we can make a confident decision about whether a session exists
  // and whether it matches the token's owner.
  //
  // Timer scheduling is subtle. Under React 18 <StrictMode> dev-mode the
  // AuthContext's mount effect runs twice, and AuthContext.check() sets
  // user to a NEW object reference on each pass. Any effect that depends
  // on `user` will thus be torn down + re-run on remount. If we latched
  // the guard BEFORE scheduling the timer, the cleanup would clear the
  // timer and the second effect run would bail on the guard — no
  // navigation would ever fire. To avoid that:
  //   • The ref is set INSIDE the setTimeout callback (i.e. only after we
  //     actually start navigating), so the guard prevents double-nav but
  //     doesn't strand an orphaned first attempt.
  //   • The cleanup calls `clearTimeout` so a truly-cancelled effect
  //     (e.g. state.step going from 'success' back to something else,
  //     which shouldn't happen but is defensively covered) doesn't leak.
  //   • Re-running the effect after StrictMode's remount simply schedules
  //     a fresh timer — either one that fires is fine because navigate()
  //     is idempotent under {replace:true}.
  useEffect(() => {
    if (autoNavRef.current) return;
    if (state.step !== "success" && state.step !== "already") return;
    if (authLoading) return;

    // MISMATCH: token was issued for a different user than the one
    // currently authenticated in this browser. Force-logout the active
    // session (clears cookies + storage tokens across the origin) and
    // redirect to /login so Coach B can log in cleanly. We do NOT wait
    // 1.5s here — no confetti/celebration screen for the wrong account.
    if (sessionMismatch) {
      autoNavRef.current = true;
      (async () => {
        try { await logout(); } catch { /* ignore — cookies cleared regardless */ }
        navigate("/login?verified=1&switched=1", { replace: true });
      })();
      return;
    }

    // No session in this browser — the user tapped the link on a device
    // where they never signed in. Point them at /login with a flag the
    // Login page uses to render a "Your email has been verified…" banner.
    const target = user
      ? (user.role === "coach" ? "/coach/dashboard"
        : user.role === "parent" ? "/parent/dashboard"
        : user.role === "platform_admin" ? "/admin/dashboard"
        : "/app")
      : "/login?verified=1";

    // Short delay so the success confirmation isn't a flicker.
    const timer = setTimeout(() => {
      autoNavRef.current = true;
      navigate(target, { replace: true });
    }, 1500);
    return () => clearTimeout(timer);
  }, [state.step, state.verifiedUserId, authLoading, user, sessionMismatch, navigate, logout]);

  // Which manual CTA button do we render? Only shown as a fallback in
  // case the auto-nav timer hasn't fired yet (or was interrupted). We
  // compute the destination the same way as the effect above so the
  // click matches the imminent auto-nav.
  const manualCta = sessionMismatch
    ? { to: "/login?verified=1&switched=1", label: "Continue to login" }
    : user
    ? {
        to:
          user.role === "coach" ? "/coach/dashboard"
          : user.role === "parent" ? "/parent/dashboard"
          : user.role === "platform_admin" ? "/admin/dashboard"
          : "/app",
        label:
          user.role === "coach" ? "Go to dashboard"
          : user.role === "parent" ? "Go to dashboard"
          : user.role === "platform_admin" ? "Open admin"
          : "Continue",
      }
    : { to: "/login?verified=1", label: "Continue to login" };

  return (
    <div className="flex min-h-screen bg-ink-100/40">
      <div className="m-auto w-full max-w-md px-6 py-10">
        <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-ink-900">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white shadow-sm">
            <ShieldCheck className="h-5 w-5" />
          </span>
          {brand.app_name}
        </Link>

        <div
          className="mt-8 rounded-2xl border border-ink-100 bg-white p-8 shadow-sm"
          style={{ position: "relative" }}
          data-testid={`verify-email-${state.step}`}
        >
          {/* Confetti burst — fires only on fresh verification (`success`)
              AND when the verified account matches the active session.
              We deliberately suppress the celebration on a mismatch so
              Coach B doesn't see fireworks over Coach A's dashboard
              transition. Respects reduced-motion. */}
          <Confetti fire={state.step === "success" && !sessionMismatch} />
          {state.step === "verifying" ? (
            <div className="flex flex-col items-center text-center">
              <Loader2 className="h-8 w-8 animate-spin text-brand" aria-hidden="true" />
              <p className="mt-4 font-semibold text-ink-900">Verifying your email…</p>
              <p className="mt-1 text-sm text-ink-500">One moment.</p>
            </div>
          ) : null}

          {state.step === "success" ? (
            <div className="flex flex-col items-center text-center">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" aria-hidden="true" />
              </span>
              <h1 className="mt-4 font-heading text-2xl font-bold text-ink-900">Email verified!</h1>
              <p className="mt-2 text-sm text-ink-700">
                Thanks for confirming <span className="font-semibold text-ink-900">{state.email}</span>. Your account is now fully active.
              </p>
              {sessionMismatch ? (
                <p
                  className="mt-3 text-xs text-ink-500"
                  data-testid="verify-session-switch-hint"
                >
                  Signing you out of the previous account and taking you to log in…
                </p>
              ) : user ? (
                <p className="mt-3 text-xs text-ink-500" data-testid="verify-auto-redirect-hint">
                  Taking you to your dashboard…
                </p>
              ) : null}
              <Link to={manualCta.to} className="mt-6 w-full" replace>
                <Button
                  className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover"
                  data-testid="verify-success-continue"
                >
                  {manualCta.label}
                </Button>
              </Link>
            </div>
          ) : null}

          {state.step === "already" ? (
            <div className="flex flex-col items-center text-center">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-50">
                <CheckCircle2 className="h-7 w-7 text-brand" aria-hidden="true" />
              </span>
              <h1 className="mt-4 font-heading text-2xl font-bold text-ink-900">You&rsquo;re already verified</h1>
              <p className="mt-2 text-sm text-ink-700">
                <span className="font-semibold text-ink-900">{state.email}</span> was verified earlier — no further action needed.
              </p>
              {sessionMismatch ? (
                <p
                  className="mt-3 text-xs text-ink-500"
                  data-testid="verify-session-switch-hint"
                >
                  Signing you out of the previous account and taking you to log in…
                </p>
              ) : user ? (
                <p className="mt-3 text-xs text-ink-500" data-testid="verify-auto-redirect-hint">
                  Taking you to your dashboard…
                </p>
              ) : null}
              <Link to={manualCta.to} className="mt-6 w-full" replace>
                <Button
                  className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover"
                  data-testid="verify-already-continue"
                >
                  {manualCta.label}
                </Button>
              </Link>
            </div>
          ) : null}

          {state.step === "failed" ? (
            <div className="flex flex-col items-center text-center">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-rose-50">
                <AlertTriangle className="h-7 w-7 text-rose-600" aria-hidden="true" />
              </span>
              <h1 className="mt-4 font-heading text-2xl font-bold text-ink-900">Link no longer valid</h1>
              <p
                className="mt-2 text-sm text-ink-700"
                data-testid="verify-failed-reason"
              >
                {state.error}
              </p>
              <p className="mt-4 text-sm text-ink-500">
                {user
                  ? "Head back to your dashboard and tap Resend verification email to get a fresh link."
                  : "Log in, then tap Resend verification email on your dashboard to get a fresh link."}
              </p>
              <Link to={user ? "/coach/dashboard" : "/login"} className="mt-6 w-full">
                <Button
                  className="h-12 w-full bg-brand font-semibold text-white hover:bg-brand-hover"
                  data-testid="verify-failed-continue"
                >
                  {user ? "Back to dashboard" : "Go to login"}
                </Button>
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
